// Shared feature-flag registry — the single source of truth for both backend
// resolution (services/features.ts) and the super-admin UI. DB-backed overrides
// (feature_overrides table) layer on top of these code defaults.

export const FEATURE_PLANS = [
  'FREE',
  'ESSENTIALS',
  'PRO',
  'ENTERPRISE',
] as const;
export type FeaturePlan = (typeof FEATURE_PLANS)[number];

// 'plan'   → "inherit" resolves to the org's billing-plan default (below).
// 'system' → "inherit" resolves to a global, DB-backed default (super-admin
//            global toggle); plan-independent. Launch gates live here.
export type FeatureKind = 'plan' | 'system';

export type FeatureDef = {
  key: string;
  label: string;
  description: string;
  kind: FeatureKind;
  // If set, this is a capability under a master gate (the parent's key). The
  // service layer already requires BOTH to be on, so a child is inert at
  // runtime unless the parent is on too. Used to group the admin UI (children
  // render indented under their parent) — it does not add enforcement.
  parent?: FeatureKey;
  // Two-axis gating (only on `kind: 'plan'` features): off for every org until
  // the launch flag opens, then GAs to plan-entitled orgs; per-org overrides win.
  launchGated?: boolean;
  // Default value per plan. For `system` features every plan is identical
  // (plan-independent baseline) — the global override is how you flip it on.
  defaults: Record<FeaturePlan, boolean>;
};

const allPlans = (value: boolean): Record<FeaturePlan, boolean> => ({
  FREE: value,
  ESSENTIALS: value,
  PRO: value,
  ENTERPRISE: value,
});

export const FEATURES: readonly FeatureDef[] = [
  // ── Plan-tier features (Inherit = billing plan) ──
  {
    key: 'alerts',
    label: 'Alerts',
    description:
      'Event-triggered Actions / notifications (Slack, email, Telegram, Discord).',
    kind: 'plan',
    defaults: { FREE: false, ESSENTIALS: true, PRO: true, ENTERPRISE: true },
  },
  {
    key: 'webhooks',
    label: 'Webhooks',
    description: 'Outbound webhook Actions.',
    kind: 'plan',
    defaults: { FREE: false, ESSENTIALS: false, PRO: true, ENTERPRISE: true },
  },
  {
    key: 'apiAccess',
    label: 'API Access',
    description:
      'Programmatic reads with an API token or OAuth client (search, analytics, companies, revenue, identities). Ingest, dashboards, and assistant (MCP) reads are free on every plan.',
    kind: 'plan',
    defaults: { FREE: false, ESSENTIALS: false, PRO: true, ENTERPRISE: true },
  },

  // ── System launch gates (Inherit = global DB default; off until ready) ──
  {
    key: 'enrichment',
    label: 'Company Enrichment',
    description:
      'Firmographic enrichment surfaces — Automations → Enrichment, Settings → Enrichment, and the company Enrich action.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'actionsCredits',
    label: 'Actions & Credits',
    description:
      'The single Actions + Data Credits gate. Powers the Billing balance UI (Actions allowance & Data Credit wallet), top-ups, auto-refill, the extra-Actions add-on, and hard enforcement (HTTP 402 on shortfall). Off = surfaces hidden and usage is record-only (no block).',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'actionsScheduler',
    label: 'Scheduled Actions',
    description:
      'Runs saved actions on a schedule (the in-process scheduler drains due actions and runs each via runOnce). Off = the "On a Schedule" trigger stores config but never fires.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'demoTopup',
    label: 'Demo Top-Up',
    description:
      'Kill switch for the synthetic demo top-up (the in-process job that keeps the demo org data fresh). ON by default; set the global override off to pause the hourly refresh. Never deletes the demo org; a demo dataset version bump wipes and reseeds its synthetic data once. No effect in environments without a demo org.',
    kind: 'system',
    defaults: allPlans(true),
  },
  {
    key: 'sealDestinationSecrets',
    label: 'Seal Destination Credentials',
    description:
      'Encrypts Slack/Discord webhook URLs, webhook signing secrets, and webhook headers saved on action and enrichment-notification destinations (KMS envelope). OFF by default; turn the global override on once every backend can read sealed destinations. On: saves seal and a background pass seals existing rows. Off again: new saves stay plain; already-sealed rows keep working.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'leanOnboarding',
    label: 'Lean Onboarding',
    description:
      'New sign-ups get the one-screen setup (website, optional goal) and land on Getting Started with the Launch Checklist; plan choice moves after value. Read globally, because a new sign-up has no org yet. Off = the Organization, Plan, Project wizard.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'introOffer',
    label: 'First Month $1 Offer',
    description:
      'Workspaces on Free that have never paid get the first paid month for $1: checkout starts a 30-day Stripe trial with a $1 one-time charge and adds starter Data Credits. Read globally so the signed-out sign-up page can mention it. Turn on only with leanOnboarding: the classic wizard checkout would apply it without showing it. Off = normal checkout.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'targetAccounts',
    label: 'Target Accounts',
    description:
      'Target Account insights (kind=account profiles): Save-from-Research, Promote-from-Company, the Target Accounts tab, and run-form prefill. Off = surfaces hidden; existing account profiles keep serving API reads.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'configurableSignals',
    label: 'Configurable Signals',
    description: 'Company signal rules, previews, and periodic evaluation.',
    kind: 'system',
    parent: 'gtmSignals',
    defaults: allPlans(false),
  },
  {
    key: 'gtmSignals',
    label: 'GTM Signals',
    description:
      'The signals layer: internal preset recipes evaluated on the event path, the signals feed + per-account timelines, and external signals emitted by Account Watch. Off = nothing is recorded and the feed is hidden.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'actionChaining',
    label: 'Signal-Triggered Actions',
    description:
      'Signal triggers on actions (an AI action can run itself when a signal fires). Off = signal triggers store config but never fire.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'visitorId',
    label: 'Visitor Identification',
    description:
      'Master gate for anonymous visitor identification at ingest. Gates the per-capability flags — visitorIdCompany today, person-level to follow. Off = no anonymous session resolves to a company or person.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'visitorIdCompany',
    label: 'Company Visitor Identification',
    description:
      'Company-level reverse-IP resolution: resolve an anonymous session IP to a company and set companyId, feeding the GTM signal recipes. Requires the visitorId master gate plus a configured visitor_company provider. Geographic scope is the org region policy (Company Visitor ID settings): explicitly-consented visitors resolve anywhere; without explicit consent the policy decides (default us_only). Off = anonymous traffic stays unresolved.',
    kind: 'system',
    parent: 'visitorId',
    defaults: allPlans(false),
  },
  {
    key: 'webResearch',
    label: 'Web Research Provider',
    description:
      'Ops kill-switch for the web-research company-enrichment provider (draws on the web-search waterfall plus an extraction model; also requires a routable web-search provider).',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'strictClientIp',
    label: 'Strict Client IP',
    description:
      "Trust only the load balancer's view of the caller's IP for event location, company lookup and rate limits, plus each project's First-Party Proxy setting and the user IP a secret-key call forwards. Off = the old behavior: client-set IP headers are trusted. Turn on for one org first, then globally, after proxied projects have First-Party Proxy on. Callers with no org (promo and deal limits, unknown keys) follow the global setting.",
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'autocapture',
    label: 'Autocapture',
    description:
      'No-code per-project event autocapture (outbound links, file downloads, form submissions, video/audio engagement) + its Project Settings surface.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'stripeApp',
    label: 'Stripe Integration',
    description:
      'Connect a Stripe account on the Revenue settings page to auto-ingest payments. Separate from Revenue Attribution: off = the Revenue page shows the Stripe integration as "coming soon" (the rest of Revenue still works via the manual SDK). Pending Stripe app review.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'searchConsole',
    label: 'Search Console',
    description:
      'Google Search Console reporting joined to company identity: the Analytics → Search page and the Project Settings → Search Console connect flow. Off = both surfaces are hidden and the report/connect endpoints 403; an existing connection stays vaulted and resumes when the flag returns.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'dashboardV2',
    label: 'Dashboard Redesign',
    description:
      'The redesigned Analytics dashboard: a metric strip that drives one chart, plus Accounts, Traffic and Events sections. Off = the current widget grid. Turn on per org first, then globally.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'ltdOffer',
    label: 'Lifetime Deal Offer',
    description:
      'Launch gate for the time-limited Lifetime Deal: the /deal promo page, its access-code gate, and LTD checkout. Off = the offer is hidden and unpurchasable. Global only (no per-org setting); turn it on for the launch window, off after. Never gates claiming or honoring an already-purchased lifetime grant; those entitlements are permanent.',
    kind: 'system',
    defaults: allPlans(false),
  },

  {
    key: 'integrationsHub',
    label: 'Integrations Hub',
    description:
      'The Integrations hub SECTION (the top-level nav, browsable connector library, and connected instances). Off = the hub nav is hidden and its routes redirect. A one-time launch gate for the new section; advanced connectors are gated separately by advancedIntegrations (plan-tiered).',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'advancedIntegrations',
    label: 'Advanced Integrations',
    description:
      'Advanced connectors in the Integrations hub (Clay, Webhook, and other advanced integrations added later): their catalog cards, connect flows, and action destinations. Plan-tiered to PRO and above. Also requires the Integrations Hub section to be on. Basic connectors (Slack, Discord, Telegram) are unaffected.',
    kind: 'plan',
    parent: 'integrationsHub',
    defaults: { FREE: false, ESSENTIALS: false, PRO: true, ENTERPRISE: true },
  },
  {
    key: 'slackApp',
    label: 'Slack App',
    description:
      'Launch gate for connecting Slack through the Logspot Slack app (Add to Slack, channel picker). Turn on once the Slack app keys are saved in Integration Apps. Off = Slack shows as coming soon in the Integrations hub; existing Slack connections and pasted Slack webhook URLs keep working.',
    kind: 'system',
    parent: 'integrationsHub',
    defaults: allPlans(false),
  },
  {
    key: 'crmIntegrations',
    label: 'CRM Integrations',
    description:
      'Launch gate for the HubSpot connector: connect from the Integrations hub and send Target Account intent to HubSpot. Off = the HubSpot card shows as coming soon. Also requires Advanced Integrations (plan) and the Integrations hub.',
    kind: 'system',
    parent: 'integrationsHub',
    defaults: allPlans(false),
  },
  {
    key: 'crmAttio',
    label: 'Attio Integration',
    description:
      'Launch gate for the Attio connector: connect from the Integrations hub and send Target Account intent to Attio companies. Off = the Attio card shows as coming soon. Also requires Advanced Integrations (plan) and the Integrations hub.',
    kind: 'system',
    parent: 'integrationsHub',
    defaults: allPlans(false),
  },
  {
    key: 'crmSalesforce',
    label: 'Salesforce Integration',
    description:
      'Launch gate for the Salesforce connector: connect from the Integrations hub and send Target Account intent to Salesforce accounts. Off = the Salesforce card shows as coming soon. Also requires Advanced Integrations (plan) and the Integrations hub.',
    kind: 'system',
    parent: 'integrationsHub',
    defaults: allPlans(false),
  },

  // ── AI launch gates (Inherit = global DB default; off until ready) ──
  {
    key: 'ai',
    label: 'AI',
    description:
      'Master AI gate — the Bedrock gateway, AI Settings page, and any AI-powered action.',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'activityDigest',
    label: 'Activity Digest',
    description:
      'Activity Digest action (deterministic template + optional AI narrative).',
    kind: 'system',
    defaults: allPlans(false),
  },
  {
    key: 'companyProfile',
    label: 'Company Profile (Profile Agent + ICP Analyst)',
    description:
      'Build Company Profile (profile.build): the Profile Agent researches the org GTM Company Profile from its website via internal enrichment + web search, and the ICP Analyst sharpens the ICP from real accounts + events (they work together; the Analyst skips itself without data).',
    kind: 'system',
    parent: 'ai',
    defaults: allPlans(false),
  },
  {
    key: 'freePlanProfileBuild',
    label: 'Free Plan: Company Profile Builds',
    description:
      'Kill switch for Company Profile builds on the Free plan (including the free first build offered in onboarding and over MCP). ON by default; set the global override off to stop Free-plan orgs from building. Paid and trialing orgs are unaffected.',
    kind: 'system',
    parent: 'ai',
    defaults: allPlans(true),
  },
  {
    key: 'prospectResearch',
    label: 'Prospect Researcher',
    description:
      'Prospect Researcher (prospect.research): parallel company + contact brief tuned to the org profile, via web search.',
    kind: 'system',
    parent: 'ai',
    defaults: allPlans(false),
  },
  {
    key: 'leadQualify',
    label: 'Lead Qualifier',
    description:
      'Lead Qualifier (lead.qualify): deterministic 0–100 fit score vs the org profile ICP + personas.',
    kind: 'system',
    parent: 'ai',
    defaults: allPlans(false),
  },
  {
    key: 'outreachDraft',
    label: 'Outreach Drafter',
    description:
      'Outreach Drafter (outreach.draft): cold email + LinkedIn sequence from a qualified prospect.',
    kind: 'system',
    parent: 'ai',
    defaults: allPlans(false),
  },
  {
    key: 'personLookup',
    label: 'Person Lookup',
    description: 'Person (people) enrichment lookup action.',
    kind: 'system',
    defaults: allPlans(false),
  },
] as const;

export type FeatureKey = (typeof FEATURES)[number]['key'];

export const FEATURE_KEYS: readonly string[] = FEATURES.map((f) => f.key);

export const FEATURE_BY_KEY: Record<string, FeatureDef> = Object.fromEntries(
  FEATURES.map((f) => [f.key, f]),
);

export const isFeatureKey = (key: string): key is FeatureKey =>
  key in FEATURE_BY_KEY;

// The launch-gated subset (two-axis: plan-tiered and shipped-dark). Drives the
// super-admin Launch Flags surface; empty until a plan feature adopts the bit.
export const LAUNCH_GATED_FEATURES: readonly FeatureDef[] = FEATURES.filter(
  (f) => f.launchGated,
);

export type FeatureSurface = 'launch' | 'plan' | 'ops' | 'promo';

// Ops toggles off the customer Launch Flags list: web-research, demo/env, credential sealing. The 6
// credentialed providers moved to per-provider toggles on the Providers pages.
const OPS_FEATURE_KEYS = new Set<string>([
  'webResearch',
  'demoTopup',
  'sealDestinationSecrets',
]);

// Which super-admin surface a feature belongs to. Plan entitlements stay on
// Subscriptions; ops/promo are grouped apart; everything else is a launch flag.
export const surfaceOf = (def: FeatureDef): FeatureSurface => {
  if (def.kind === 'plan' && !def.launchGated) return 'plan';
  if (def.key === 'ltdOffer') return 'promo';
  if (OPS_FEATURE_KEYS.has(def.key)) return 'ops';
  return 'launch';
};

// Ops and promo flags are only read globally, so an org override does nothing.
export const isGlobalOnly = (def: FeatureDef): boolean => {
  const surface = surfaceOf(def);
  return surface === 'ops' || surface === 'promo';
};

// The customer Launch Flags list: system customer capabilities plus any plan
// feature that is launch-gated (two-axis).
export const LAUNCH_SURFACE_FEATURES: readonly FeatureDef[] = FEATURES.filter(
  (f) => surfaceOf(f) === 'launch',
);

const PLAN_LABEL: Record<FeaturePlan, string> = {
  FREE: 'Free',
  ESSENTIALS: 'Essentials',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

// Human label for who a launch turns a feature on for: 'All plans' for system
// (plan-independent) features, or the entitled tier for launch-gated ones.
export const launchScopeLabel = (def: FeatureDef): string => {
  if (def.kind === 'system') return 'All plans';
  const entitled = FEATURE_PLANS.filter((p) => def.defaults[p]);
  if (entitled.length === 0) return 'No plans';
  if (entitled.length === FEATURE_PLANS.length) return 'All plans';
  if (entitled.length === 1) return PLAN_LABEL[entitled[0]!];
  const firstIdx = FEATURE_PLANS.indexOf(entitled[0]!);
  const isSuffix = entitled.length === FEATURE_PLANS.length - firstIdx;
  return isSuffix
    ? `${PLAN_LABEL[entitled[0]!]} & up`
    : entitled.map((p) => PLAN_LABEL[p]).join(', ');
};

// The per-org/global effective view returned to the super-admin UI.
export type FeatureOverrideScope = 'org' | 'global';

export type FeatureOverrideState = {
  // Where the active override (if any) is set. Absent → inheriting.
  scope: FeatureOverrideScope | null;
  value: boolean | null;
  setByUserId?: string | null;
  reason?: string | null;
};

export type ResolvedFeature = {
  key: string;
  effective: boolean;
  // Whether the effective value comes from a per-org override, the global
  // default, or the plan/system code default (i.e. "inherited").
  source: 'org' | 'global' | 'inherited';
};

// ── Effective-value resolution (pure) ────────────────────

// Shared with services/features.ts so the layering stays unit-testable.
// Precedence: per-org override, then (launch gate | global '*'), then plan default.
export type FeatureLayers = {
  org: Record<string, boolean>;
  global: Record<string, boolean>;
  launch: Record<string, boolean>;
};

const planDefaultOf = (def: FeatureDef, plan: FeaturePlan): boolean =>
  def.defaults[plan] ?? false;

// Effective on/off for one feature at a given plan.
export const resolveOneEffective = (
  def: FeatureDef,
  layers: FeatureLayers,
  plan: FeaturePlan,
): boolean => {
  if (def.key in layers.org) return layers.org[def.key]!;
  const planDefault = planDefaultOf(def, plan);
  if (def.launchGated) {
    // The global '*' override is ignored for launch-gated features; the launch
    // flag is the only global switch, AND-gated with the plan default.
    return layers.launch[def.key] === true ? planDefault : false;
  }
  return def.key in layers.global ? layers.global[def.key]! : planDefault;
};

// Effective map for the whole registry at a given plan.
export const resolveEffective = (
  registry: readonly FeatureDef[],
  layers: FeatureLayers,
  plan: FeaturePlan,
): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  for (const def of registry)
    out[def.key] = resolveOneEffective(def, layers, plan);
  return out;
};

// ── Super-admin launch-flag / override views ─────────────

// One launch-gated feature's global launch state plus audit, for the UI.
export type LaunchFlagView = {
  key: string;
  label: string;
  description: string;
  // Who a launch turns it on for ('All plans' or a tier like 'Pro & up').
  scope: string;
  launched: boolean;
  setByUserId?: string | null;
  reason?: string | null;
  updatedAt?: string | null;
};

// An org holding a per-org override on a feature (the beta-override roster).
export type FeatureOverrideOrg = {
  organizationId: string;
  organizationName: string | null;
  value: boolean;
  setByUserId?: string | null;
  reason?: string | null;
};

// A leftover override row whose feature key is no longer in the registry.
export type StrandedOverride = {
  organizationId: string;
  featureKey: string;
  value: boolean;
  setByUserId?: string | null;
  reason?: string | null;
};
